// Admin gate. Resolves the caller's admin org membership BEFORE any admin RPC fires.
// Defense in depth: if any admin RPC slipped its 42501 check, this guard already
// redirected non-admins to /app/employee.
//
// FORKED — does NOT import useEmployee or any src/lib/pulse/** module.
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

type AdminState =
  | { status: 'loading' }
  | { status: 'ready'; orgId: string }
  | { status: 'not_admin' }
  | { status: 'error'; message: string };

const ACTIVE_KEY = 'admin.activeOrgId';

export function getActiveAdminOrg(): string | null {
  try { return localStorage.getItem(ACTIVE_KEY); } catch { return null; }
}
export function setActiveAdminOrg(orgId: string | null) {
  try {
    if (orgId) localStorage.setItem(ACTIVE_KEY, orgId);
    else localStorage.removeItem(ACTIVE_KEY);
  } catch { /* ignore */ }
}

export function useAdminGuard(): AdminState {
  const { user, isLoading: authLoading } = useAuth();
  const [state, setState] = useState<AdminState>({ status: 'loading' });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setState({ status: 'loading' });
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('pulse_org_members')
        .select('org_id, role')
        .eq('user_id', user.id)
        .eq('role', 'admin');
      if (cancelled) return;
      if (error) { setState({ status: 'error', message: error.message }); return; }
      const orgs = (data ?? []).map((r) => r.org_id).filter(Boolean) as string[];
      if (orgs.length === 0) { setState({ status: 'not_admin' }); return; }
      const preferred = getActiveAdminOrg();
      const chosen = preferred && orgs.includes(preferred) ? preferred : orgs[0];
      setState({ status: 'ready', orgId: chosen });
    })();
    return () => { cancelled = true; };
  }, [user, authLoading]);

  return state;
}
